<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useDisplay } from "vuetify";
import CreateAccountForm from "@/components/Account/CreateAccountForm.vue";
import PortalNavigationDrawer from "@/components/App/PortalNavigationDrawer.vue";
import AccountSelectionDialog from "@/components/WebLogin/AccountSelectionDialog.vue";
import { useServiceWorkerUpdate } from "@/composables/App/useServiceWorkerUpdate";
import { usePortalSecuritySettings } from "@/composables/Security/usePortalSecuritySettings";
import type WebLoginAccount from "@/interfaces/WebLogin/WebLoginAccount";
import { setAccountLastLogin } from "@/repositories/Web/WebLoginRepository";
import { getAccountNameInitials } from "@/helpers/WebLogin/WebLoginHelpers";
import { useAlertsStore } from "@/stores/alerts";
import { useAuthStore } from "@/stores/auth";
import { useNotificationsStore } from "@/stores/notifications";
import { useParametersStore } from "@/stores/parameters";

const route = useRoute();
const router = useRouter();
const { smAndDown, xs } = useDisplay();
const alertsStore = useAlertsStore();
const authStore = useAuthStore();
const notificationsStore = useNotificationsStore();
const parametersStore = useParametersStore();
const securitySettings = usePortalSecuritySettings();
const { refreshApp, updateExists } = useServiceWorkerUpdate();

const appVersion = __APP_VERSION__;

const showDrawer = ref(false);
const showAccountSelectionDialog = ref(false);
const showAddAccountDialog = ref(false);

const appBarTitle = computed(() => {
  return (route.meta.displayName || route.meta.title || "Grower Portal") as string;
});

const showAppBars = computed(() => {
  return (
    Boolean(route.name) &&
    !["login", "reset-password", "install-app-how-to"].includes(
      String(route.name)
    )
  );
});

const accountInitials = computed(() =>
  getAccountNameInitials(
    authStore.selectedAccount?.name || authStore.webLogin.webLoginUserName || ""
  )
);

const accountName = computed(
  () =>
    authStore.selectedAccount?.name ||
    authStore.webLogin.employeeManagerName ||
    authStore.webLogin.webLoginUserName ||
    "Account"
);

const showNotification = computed({
  get: () => notificationsStore.notification !== undefined,
  set: (value) => {
    if (!value) {
      notificationsStore.clearNotification();
    }
  },
});

const requiresAccountSelection = computed(() => {
  return showAppBars.value && authStore.isAuthenticated && !authStore.selectedAccount;
});

watch(
  () => route.fullPath,
  () => {
    if (smAndDown.value) {
      showDrawer.value = false;
    }
  }
);

watch(
  requiresAccountSelection,
  async (value) => {
    if (!value) {
      showAccountSelectionDialog.value = false;
      return;
    }

    if (securitySettings.manager.value) {
      await authStore.setSelectedAccount(
        {
          id: "",
          name:
            authStore.webLogin.employeeManagerName ||
            authStore.webLogin.webLoginUserName ||
            "Manager",
          entityId: "",
          address1: "",
          address2: "",
          city: "",
          state: "",
          zip: "",
          phone: "",
          apEmail: "",
          creditLimt: 0,
          currentBalance: 0,
          customerType: "",
          isAccountLinkedToWebLogin: false,
          canSwitchAccounts: true,
          isCgCustomer: false,
        },
        false
      );
      showAccountSelectionDialog.value = false;
      return;
    }

    showAccountSelectionDialog.value = true;
  },
  { immediate: true }
);

async function logOut() {
  authStore.clearState();
  await router.push("/login");
}

async function goToResetPassword() {
  await router.push("/resetPassword");
}

function openAccountSelection() {
  showAccountSelectionDialog.value = true;
}

function openAddAccount() {
  showAddAccountDialog.value = true;
}

async function selectAddedAccount(account: WebLoginAccount) {
  const accountToSelect = {
    ...account,
    canSwitchAccounts: true,
  };

  await authStore.setSelectedAccount(accountToSelect);

  if (accountToSelect.isAccountLinkedToWebLogin && accountToSelect.id) {
    await setAccountLastLogin(accountToSelect.id);
  }

  showAddAccountDialog.value = false;
}
</script>

<template>
  <v-app>
    <div v-if="parametersStore.isAnyLoading" class="text-center mt-5">
      <v-progress-circular
        class="mx-auto"
        color="primary"
        :size="50"
        :width="2"
        indeterminate
      />
    </div>

    <v-app-bar v-if="showAppBars" app color="primary">
      <v-app-bar-nav-icon @click="showDrawer = !showDrawer" />
      <v-toolbar-title>{{ appBarTitle }}</v-toolbar-title>
      <v-spacer />

      <v-menu v-if="authStore.isAuthenticated" location="bottom end">
        <template #activator="{ props }">
          <v-btn v-bind="props" icon>
            <v-avatar color="white">
              <span v-if="accountInitials" class="text-primary font-weight-bold">
                {{ accountInitials }}
              </span>
              <v-icon v-else color="primary">mdi-account</v-icon>
            </v-avatar>
          </v-btn>
        </template>

        <v-card min-width="240">
          <v-card-title class="text-wrap">
            {{ accountName }}
          </v-card-title>
          <v-divider />
          <v-list density="compact">
            <v-list-item
              v-if="authStore.selectedAccount?.canSwitchAccounts"
              @click="openAccountSelection"
            >
              <v-list-item-title>Switch Account</v-list-item-title>
            </v-list-item>
            <v-list-item @click="openAddAccount">
              <v-list-item-title>Add Account</v-list-item-title>
            </v-list-item>
            <v-list-item @click="goToResetPassword">
              <v-list-item-title>Change Password</v-list-item-title>
            </v-list-item>
            <v-list-item @click="logOut">
              <v-list-item-title class="text-error">Log Out</v-list-item-title>
            </v-list-item>
            <v-list-item>
              <v-list-item-title class="text-medium-emphasis">
                Version {{ appVersion }}
              </v-list-item-title>
            </v-list-item>
          </v-list>
        </v-card>
      </v-menu>
    </v-app-bar>

    <PortalNavigationDrawer
      v-if="showAppBars"
      v-model="showDrawer"
    />

    <AccountSelectionDialog
      v-model="showAccountSelectionDialog"
    />

    <v-dialog
      v-model="showAddAccountDialog"
      :fullscreen="xs"
      scrollable
      max-width="500"
      persistent
    >
      <CreateAccountForm
        v-if="showAddAccountDialog"
        :is-adding-account-to-web-login="true"
        :on-changed="showAddAccountDialog"
        title="Add Account"
        @on-cancel="showAddAccountDialog = false"
        @on-added="selectAddedAccount"
      />
    </v-dialog>

    <v-main>
      <v-container fluid>
        <v-alert
          v-if="securitySettings.viewOnly.value"
          class="mb-4"
          type="error"
          variant="tonal"
        >
          You are logged in as an employee and currently have view-only access.
        </v-alert>

        <v-alert
          v-if="alertsStore.alert"
          class="mb-4"
          :type="alertsStore.alert.type"
          variant="tonal"
          closable
          @click:close="alertsStore.clearAlert"
        >
          <div class="font-weight-medium">
            {{ alertsStore.alert.headerMessage }}
          </div>
          <div v-if="alertsStore.alert.bodyMessage">
            {{ alertsStore.alert.bodyMessage }}
          </div>
        </v-alert>

        <router-view />

        <v-snackbar
          v-model="showNotification"
          :color="notificationsStore.notification?.type"
          location="bottom right"
          :timeout="6000"
        >
          {{ notificationsStore.notification?.message }}
          <template #actions>
            <v-btn variant="text" @click="showNotification = false">
              Close
            </v-btn>
          </template>
        </v-snackbar>

        <v-snackbar
          :model-value="updateExists"
          location="bottom right"
          timeout="-1"
          color="primary"
        >
          An update is available.
          <template #actions>
            <v-btn variant="text" @click="refreshApp">Update</v-btn>
          </template>
        </v-snackbar>
      </v-container>
    </v-main>

    <v-footer v-if="!showAppBars" app>
      <v-container class="text-center text-medium-emphasis">
        Version {{ appVersion }}
      </v-container>
    </v-footer>
  </v-app>
</template>
